import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAPI } from '../context/APIContext';
import { useConversation } from '../context/ConversationContext';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);
  const api = useAPI();
  const [models, setModels] = useState<any[]>([]);
  const { selectedMode } = useConversation();

  const companyName = process.env.REACT_APP_COMPANY_NAME || 'RazorpayGPT';

  // Load models dynamically from API
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const data = await api.getModels();
        setModels(data);
      } catch (error) {
        console.error('Error fetching models:', error);
        // Fallback to default models if API fails
        setModels([
          { id: "gpt-4o", name: "GPT-4o" },
        ]);
      }
    };

    fetchModels();
  }, [api]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1280;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="h-screen w-full flex bg-white overflow-hidden relative">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        isMobile={isMobile}
        models={models}
      />
      
      {/* Main content */}
      <div className="flex-1 flex flex-col h-full">
        {/* Mobile header with menu button - only show when sidebar is hidden */}
        {isMobile && !sidebarOpen && (
          <div className="p-3 sm:p-4 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <button 
                  onClick={() => setSidebarOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-8 sm:h-9 rounded-md px-2 sm:px-3 text-gray-500 hover:text-gray-700 flex-shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 truncate">{companyName}</h2>
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 mt-1">
                    <div className="inline-flex items-center rounded-full border px-2 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-primary/80 bg-green-100 text-green-700 border-green-200 text-xs capitalize">
                      {selectedMode} Mode
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </div>
      
      {/* Mobile sidebar backdrop - only show on very small screens */}
      {isMobile && sidebarOpen && window.innerWidth < 768 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 